/**
 * Marketing Terminal — Listening Feed
 *
 * Discovery sub-system. Cowork's scanner pushes alerts via the agent bridge;
 * admin reviews, dismisses, or promotes to content_ideas.
 *
 * Master spec §2.3 Discovery + audit gap F5 (false-positive filter).
 *
 * Backend:
 *   GET    /api/admin/marketing/listening
 *   PUT    /api/admin/marketing/listening/:id
 *   POST   /api/admin/marketing/listening/:id/promote-to-idea
 */

import React, { useState } from 'react';
import {
  AlertCircle,
  Check,
  ExternalLink,
  Lightbulb,
  Search,
  Settings as SettingsIcon,
  TrendingUp,
  X,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Alert {
  id: string;
  platform: string;
  source_url: string;
  source_author: string | null;
  alert_type: string;
  title: string | null;
  snippet: string | null;
  matched_keywords: string[];
  relevance_score: number;
  sentiment_score: number | null;
  pillar_matches: string[];
  suggested_action: string;
  status: string;
  detected_at: string;
}

interface AlertsResponse {
  success: boolean;
  data: Alert[];
  counts_by_status: Record<string, number>;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });

export function ListeningPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <ListeningInner />
    </QueryClientProvider>
  );
}

function ListeningInner() {
  const [tab, setTab] = useState<'feed' | 'config'>('feed');

  return (
    <div className="space-y-4">
      <div>
        <h1
          className="text-2xl font-bold"
          style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}
        >
          Listening
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Cowork scans Reddit, HN, X, news + podcast directories every few hours and pushes
          relevant alerts here. Promote to ideas or dismiss.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: 'feed', label: 'Alert Feed', icon: TrendingUp },
          { key: 'config', label: 'Watch List Settings', icon: SettingsIcon },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key as 'feed' | 'config')}
              className="flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors"
              style={
                active
                  ? { borderColor: 'var(--cedar-primary)', color: 'var(--cedar-primary)' }
                  : { borderColor: 'transparent', color: 'var(--text-secondary)' }
              }
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'feed' && <FeedTab />}
      {tab === 'config' && <ConfigTab />}
    </div>
  );
}

function FeedTab() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('new');
  const [platformFilter, setPlatformFilter] = useState('');
  const [minRelevance, setMinRelevance] = useState(0.4);

  const { data, isLoading } = useQuery<AlertsResponse>({
    queryKey: ['marketing-listening', statusFilter, platformFilter, minRelevance],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (platformFilter) qs.set('platform', platformFilter);
      qs.set('min_relevance', String(minRelevance));
      return adminFetch<AlertsResponse>(`/api/admin/marketing/listening?${qs.toString()}`);
    },
    refetchInterval: 60_000,
  });

  const review = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      adminFetch(`/api/admin/marketing/listening/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-listening'] }),
  });

  const promote = useMutation({
    mutationFn: (id: string) =>
      adminFetch(`/api/admin/marketing/listening/${id}/promote-to-idea`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-listening'] }),
  });

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-3"
        style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}
      >
        <div className="flex gap-1">
          {['new', 'reviewed', 'promoted_to_idea', 'dismissed', ''].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className="rounded px-2 py-1 text-xs font-medium"
              style={
                statusFilter === s
                  ? { backgroundColor: 'var(--cedar-primary)', color: '#fff' }
                  : { backgroundColor: 'var(--cedar-limestone)', color: 'var(--text-secondary)' }
              }
            >
              {s ? s.replace(/_/g, ' ') : 'all'}
              {s && data?.counts_by_status?.[s] !== undefined && (
                <span className="ml-1 text-[10px] opacity-75">{data.counts_by_status[s]}</span>
              )}
            </button>
          ))}
        </div>
        <select
          value={platformFilter}
          onChange={e => setPlatformFilter(e.target.value)}
          className="rounded border border-gray-200 px-2 py-1 text-xs"
        >
          <option value="">All platforms</option>
          {['reddit', 'hn', 'x', 'news', 'podcast', 'youtube'].map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Min relevance:</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={minRelevance}
            onChange={e => setMinRelevance(parseFloat(e.target.value))}
            className="w-24"
          />
          <span className="font-mono">{minRelevance.toFixed(1)}</span>
        </div>
      </div>

      {/* Feed */}
      {isLoading && <div className="text-sm text-gray-500">Loading alerts...</div>}
      {!isLoading && (data?.data?.length ?? 0) === 0 && (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
          <Search className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          No alerts match current filters.
        </div>
      )}

      {data?.data?.map(alert => (
        <AlertCard
          key={alert.id}
          alert={alert}
          onReview={status => review.mutate({ id: alert.id, status })}
          onPromote={() => promote.mutate(alert.id)}
          isPromoting={promote.isPending}
        />
      ))}
    </div>
  );
}

function AlertCard({
  alert,
  onReview,
  onPromote,
  isPromoting,
}: {
  alert: Alert;
  onReview: (status: string) => void;
  onPromote: () => void;
  isPromoting: boolean;
}) {
  const relColor =
    alert.relevance_score >= 0.8 ? 'var(--cedar-primary)' :
    alert.relevance_score >= 0.5 ? 'var(--status-warning)' :
    'var(--text-tertiary)';

  return (
    <div className="rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span
              className="rounded px-2 py-0.5 text-[10px] font-medium uppercase"
              style={{ backgroundColor: 'var(--cedar-limestone)', color: 'var(--text-secondary)' }}
            >
              {alert.platform}
            </span>
            <span
              className="rounded px-2 py-0.5 text-[10px] font-medium"
              style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
            >
              {alert.alert_type.replace(/_/g, ' ')}
            </span>
            {alert.suggested_action && alert.suggested_action !== 'monitor' && (
              <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--status-warning)' }}>
                <AlertCircle className="mr-0.5 inline h-2.5 w-2.5" />
                {alert.suggested_action.replace(/_/g, ' ')}
              </span>
            )}
            <span className="ml-auto text-[10px] text-gray-400">
              {new Date(alert.detected_at).toLocaleString()}
            </span>
          </div>

          {alert.title && (
            <div className="text-sm font-semibold text-gray-900">{alert.title}</div>
          )}
          {alert.snippet && (
            <div className="mt-1 text-xs text-gray-700 line-clamp-3">{alert.snippet}</div>
          )}
          {alert.source_author && (
            <div className="mt-1 text-[10px] text-gray-500">by {alert.source_author}</div>
          )}
          {alert.matched_keywords?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {alert.matched_keywords.map(k => (
                <code key={k} className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">{k}</code>
              ))}
            </div>
          )}
          {alert.pillar_matches?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {alert.pillar_matches.map(p => (
                <span
                  key={p}
                  className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Relevance gauge */}
        <div className="flex flex-col items-end gap-1">
          <div className="text-right text-[10px] font-medium" style={{ color: relColor }}>
            {Math.round(alert.relevance_score * 100)}%
          </div>
          <div className="h-1 w-16 rounded bg-gray-100">
            <div
              className="h-full rounded"
              style={{ width: `${alert.relevance_score * 100}%`, backgroundColor: relColor }}
            />
          </div>
          <div className="text-[10px] text-gray-400">relevance</div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <a
          href={alert.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <ExternalLink className="h-3 w-3" /> Open source
        </a>
        <div className="flex gap-2">
          {alert.status === 'new' && (
            <>
              <button
                onClick={() => onReview('dismissed')}
                className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
              >
                <X className="mr-0.5 inline h-3 w-3" />
                Dismiss
              </button>
              <button
                onClick={() => onReview('reviewed')}
                className="rounded px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                <Check className="mr-0.5 inline h-3 w-3" />
                Mark reviewed
              </button>
            </>
          )}
          {alert.status !== 'promoted_to_idea' && alert.status !== 'dismissed' && (
            <button
              onClick={onPromote}
              disabled={isPromoting}
              className="rounded px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: 'var(--cedar-primary)' }}
            >
              <Lightbulb className="mr-0.5 inline h-3 w-3" />
              {isPromoting ? '...' : 'Promote to idea'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Watch List Config tab ────────────────────────────────────────────────────

interface ConfigEntry {
  id: string;
  config_type: string;
  value: string;
  priority: string;
  geography: string[];
  is_active: boolean;
  notes: string | null;
}
interface ConfigResponse { data: Record<string, ConfigEntry[]>; }

function ConfigTab() {
  const queryClient = useQueryClient();
  const { data } = useQuery<ConfigResponse>({
    queryKey: ['marketing-listening-config'],
    queryFn: () => adminFetch<ConfigResponse>('/api/admin/marketing/listening/config'),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [newType, setNewType] = useState('keyword');
  const [newValue, setNewValue] = useState('');
  const [newPriority, setNewPriority] = useState('normal');

  const createConfig = useMutation({
    mutationFn: () =>
      adminFetch('/api/admin/marketing/listening/config', {
        method: 'POST',
        body: JSON.stringify({
          config_type: newType,
          value: newValue,
          priority: newPriority,
          geography: ['US', 'CA'],
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      setNewValue('');
      setShowAdd(false);
      queryClient.invalidateQueries({ queryKey: ['marketing-listening-config'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      adminFetch(`/api/admin/marketing/listening/config/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-listening-config'] }),
  });

  const groups = data?.data ?? {};

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="rounded px-3 py-1.5 text-xs font-medium text-white"
          style={{ backgroundColor: 'var(--cedar-primary)' }}
        >
          + Add watch list entry
        </button>
      </div>

      {showAdd && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-3 gap-2">
            <select value={newType} onChange={e => setNewType(e.target.value)} className="rounded border border-gray-200 px-2 py-1.5 text-xs">
              <option value="keyword">keyword</option>
              <option value="subreddit">subreddit</option>
              <option value="hn_topic">hn_topic</option>
              <option value="x_account">x_account</option>
              <option value="competitor">competitor</option>
              <option value="news_source">news_source</option>
              <option value="news_keyword">news_keyword</option>
              <option value="podcast_keyword">podcast_keyword</option>
            </select>
            <input
              placeholder="Value"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              className="rounded border border-gray-200 px-2 py-1.5 text-xs"
            />
            <select value={newPriority} onChange={e => setNewPriority(e.target.value)} className="rounded border border-gray-200 px-2 py-1.5 text-xs">
              <option value="low">low</option>
              <option value="normal">normal</option>
              <option value="high">high</option>
            </select>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => createConfig.mutate()}
              disabled={!newValue || createConfig.isPending}
              className="rounded px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: 'var(--cedar-primary)' }}
            >
              {createConfig.isPending ? '...' : 'Add'}
            </button>
            <button onClick={() => setShowAdd(false)} className="text-xs text-gray-500">Cancel</button>
            {createConfig.isError && (
              <span className="text-xs text-red-600">{(createConfig.error as Error)?.message}</span>
            )}
          </div>
        </div>
      )}

      {Object.entries(groups).map(([type, entries]) => (
        <div key={type} className="rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
            {type.replace(/_/g, ' ')} ({entries.length})
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {entries.map(e => (
              <div
                key={e.id}
                className={`group flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs ${
                  e.priority === 'high' ? 'ring-1 ring-green-200' : ''
                }`}
              >
                <span className={e.is_active ? 'text-gray-800' : 'text-gray-400 line-through'}>
                  {e.value}
                </span>
                {e.priority === 'high' && (
                  <span className="text-[9px] uppercase" style={{ color: 'var(--cedar-primary)' }}>HIGH</span>
                )}
                <button
                  onClick={() => {
                    if (confirm(`Remove ${e.value}?`)) remove.mutate(e.id);
                  }}
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <X className="h-3 w-3 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ListeningPage;
